import { Box, ButtonBase, Typography } from '@mui/material';
import { Add } from 'iconsax-react';
import { type FC, useCallback, useEffect, useRef, useState } from 'react';

import { hasValue } from '@core/utils/equality';
import { QUICK_ACTION_ITEMS, QuickAction } from '@web/constants/QuickAction';
import { useQuickActions } from '@web/providers/QuickActionsProvider/QuickActionsProvider';

import { QuickActionsDropdown } from './QuickActionsDropdown';

const isMac = (
  'userAgentData' in navigator
    ? (navigator.userAgentData as { platform: string }).platform
    : navigator.userAgent
)
  .toUpperCase()
  .includes('MAC');

export const QuickActionsButton: FC = () => {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLButtonElement>(null);

  const handleToggle = useCallback(() => {
    setOpen((prev) => !prev);
  }, []);

  const handleClose = useCallback(() => {
    setOpen(false);
  }, []);

  const {
    openProjectDrawer,
    openCompanyDrawer,
    openContactDrawer,
    openActivityDrawer,
    openQuoteDrawer,
  } = useQuickActions();

  const handleAction = useCallback(
    (id: QuickAction) => {
      switch (id) {
        case QuickAction.Project:
          openProjectDrawer();
          break;
        case QuickAction.Company:
          openCompanyDrawer();
          break;
        case QuickAction.Contact:
          openContactDrawer();
          break;
        case QuickAction.Activity:
          openActivityDrawer();
          break;
        case QuickAction.Quote:
          openQuoteDrawer();
          break;
        default:
          break;
      }
    },
    [openProjectDrawer, openCompanyDrawer, openContactDrawer, openActivityDrawer, openQuoteDrawer],
  );

  useEffect(() => {
    const handler = (e: KeyboardEvent): void => {
      // Skip shortcuts when user is typing in a form field or rich-text editor
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;
      if ((e.target as HTMLElement)?.isContentEditable) return;

      // Cmd+K / Ctrl+K toggles the dropdown
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        handleToggle();
        return;
      }

      // Single-key shortcuts when dropdown is open
      if (!open) return;
      const upperKey = e.key.toUpperCase();
      const match = QUICK_ACTION_ITEMS.find((item) => item.shortcutKey === upperKey);
      if (hasValue(match)) {
        e.preventDefault();
        handleAction(match.id);
        handleClose();
      }
    };
    document.addEventListener('keydown', handler);
    return (): void => document.removeEventListener('keydown', handler);
  }, [open, handleToggle, handleClose, handleAction]);

  return (
    <>
      <ButtonBase
        ref={anchorRef}
        onClick={handleToggle}
        sx={(theme) => ({
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          height: '40px',
          padding: '0 16px',
          borderRadius: '10px',
          backgroundColor: theme.palette.SlabIndigo[500],
          color: theme.palette.SlabGray[0],
          '&:hover': {
            backgroundColor: theme.palette.SlabIndigo[600],
            filter: 'brightness(1.12)',
          },
        })}
      >
        <Add size={20} color='white' />
        <Typography
          sx={(theme) => ({
            fontSize: '14px',
            fontWeight: 500,
            whiteSpace: 'nowrap',
            color: theme.palette.SlabGray[0],
          })}
        >
          CREATE NEW
        </Typography>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '20px',
            padding: '0 6px',
            borderRadius: '6px',
            backgroundColor: 'rgba(0,0,0,0.2)',
            marginLeft: '4px',
          }}
        >
          <Typography sx={{ fontSize: '11px', color: 'inherit', lineHeight: 1 }}>
            {isMac ? '⌘K' : 'Ctrl+K'}
          </Typography>
        </Box>
      </ButtonBase>

      <QuickActionsDropdown
        anchorEl={anchorRef.current}
        open={open}
        onClose={handleClose}
        onAction={handleAction}
      />
    </>
  );
};
